<script setup lang="ts">
import { useKeenSlider } from 'keen-slider/vue'
import 'keen-slider/keen-slider.min.css'

const props = defineProps<{
  data: {
    id: string
    form: string
    color_mode: 'light' | 'dark'
    translations: DirectusTranslation<{
      form_title: string
      label: string
      text: string
    }>
    images: {
      directus_files_id: DirectusFile
    }[]
  }
}>()

const [container, slider] = useKeenSlider({
  loop: true,
  renderMode: 'performance',
  drag: true,
  slides: {
    perView: 1,
  },
})

function nextSlide() {
  slider?.value?.next()
}
function prevSlide() {
  slider?.value?.prev()
}

const { data: form } = await useForm(props.data.form)

const formData = ref<any>({})

const { $i18n, $toast } = useNuxtApp()
const { dt } = useDirectusTranslation()
const { hyphen } = useHyphen()

const id = useId()

const domParser = import.meta.client ? new DOMParser() : null

const formSubmitData = computed(() => {
  const data: {
    [key: string]: {
      type: string
      value: any
      label: string
    }
  } = {}
  if (!form.value?.form_inputs)
    return data
  for (const input of form.value?.form_inputs) {
    if (input.collection === 'form_divider')
      continue

    const dataObject = {
      type: input.collection,
      value: formData.value[input.item.key],
      label: '',
    }
    if (input.collection === 'form_special_dealer_selector')
      dataObject.label = input.item.key
    else
      dataObject.label = domParser?.parseFromString(dt(input.item.translations)?.label ?? '', 'text/html').body.textContent ?? ''

    data[input.item.key as string] = dataObject
  }
  return {
    form_id: form.value?.id,
    data,
    language: $i18n.locale.value,
  }
})

const { t } = useI18n()

const formCompleted = ref(false)
const isSubmitting = ref(false)

async function handleSubmit() {
  try {
    if (isSubmitting.value)
      return

    isSubmitting.value = true
    const _result = await $fetch('/server/form', {
      method: 'POST',
      body: formSubmitData.value,
    })
    formCompleted.value = true
    $toast(t('form.success.title'))
  }
  catch (error) {
    console.error(error)
    $toast.error(t('form.error.title'), {
      description: t('form.error.description'),
    })
  }
  finally {
    isSubmitting.value = false
  }
}

const formHasDealerSelector = computed<boolean>(() => {
  if (form.value === null) {
    return false
  }
  return form.value?.form_inputs.some(input => input.collection === 'form_special_dealer_selector') ?? false
})

const showStep1 = ref(formHasDealerSelector.value)

watch(showStep1, async (value) => {
  if (value === false) {
    await nextTick()
    slider.value?.update()
  }
})

const mounted = useMounted()
const activatedTeleport = ref(false)
watch(mounted, async (value) => {
  if (value) {
    await nextTick()
    activatedTeleport.value = true
  }
})

const submitClasses = computed(() => {
  if (isSubmitting.value) {
    return 'opacity-50 mt-3rem'
  }
  return 'mt-3rem'
})
</script>

<template>
  <BlockBase
    :is-light="props.data.color_mode === 'light'"
    class="bg-starline-secondary py-2rem block-light:bg-neutral-white block-light:text-neutral-black"
  >
    <Container class="w-full">
      <div
        v-show="showStep1"
        :id="`teleport-${id}`"
      />

      <div
        v-if="!formCompleted"
        v-show="!showStep1"
        class="grid grid-cols-1 w-full gap-10rem lg:grid-cols-2"
        :class="!props.data.images.length ? 'mx-auto' : 'lg:grid-cols-2'"
      >
        <!-- SLIDER -->
        <div class="flex flex-col">
          <button
            v-if="formHasDealerSelector"
            class="mb-2rem ml-auto mt-auto block border border-neutral-white px-2.5rem py-1.25rem text-center text-1.3rem text-neutral-black text-neutral-white tracking-4px uppercase transition duration-300 block-light:border-neutral-black block-light:text-neutral-black hover:bg-neutral-white hover:text-neutral-black block-light:hover:bg-neutral-black block-light:hover:text-neutral-white"
            @click="showStep1 = true"
          >
            {{ t('form.dealers.back') }}
          </button>

          <div :id="`teleport-left-${id}`" />

          <div
            v-if="props.data.images.length"
            ref="container"
            class="keen-slider my-auto h-65rem w-full"
          >
            <div
              v-for="image, index in props.data.images"
              :key="index"
              class="keen-slider__slide size-full"
            >
              <NuxtImg
                v-if="image.directus_files_id"
                loading="lazy"
                provider="directus"
                height="650"
                width="650"
                :src="image.directus_files_id.id"
                :alt="image.directus_files_id.description"
                class="h-full w-full object-cover"
              />
            </div>

            <div
              v-if="props.data.images.length > 1"
              class="absolute bottom-0 right-0 h-10rem w-15rem flex items-center justify-center bg-starline-secondary block-light:bg-neutral-white"
            >
              <button
                class="h-10rem w-7.5rem flex items-center justify-center transition hover:bg-neutral-black/10 block-light:hover:bg-starline-secondary/10"
                @click="prevSlide"
              >
                <IconSmallArrow class="h-2rem w-2rem" />
              </button>

              <button
                class="h-10rem w-7.5rem flex items-center justify-center transition hover:bg-starline-secondary/10"
                @click="nextSlide"
              >
                <IconSmallArrow class="h-2rem w-2rem rotate-180" />
              </button>
            </div>
          </div>
        </div>

        <!-- Content -->
        <ClientOnly>
          <FormKit
            :id="id"
            v-model="formData"
            :name="`form-${id}`"
            type="form"
            class="my-auto w-full"
            :submit-label="dt(form?.submit_translations)?.text ?? ''"
            :submit-attrs="{
              outerClass: submitClasses,
              id: `submit-${id}`,
              name: `submit-${id}`,
              disabled: isSubmitting,
            }"
            :classes="{
              messages: 'mt-3rem mb-1rem',
            }"
            :disabled="isSubmitting"
            @submit="handleSubmit"
          >
            <h1 class="text-size-7.6rem font-title">
              {{ hyphen(dt(props.data.translations)?.form_title ?? '') }}
            </h1>
            <div class="grid grid-cols-2 gap-x-2rem gap-y-3rem">
              <template
                v-for="input in form?.form_inputs"
                :key="input.id"
              >
                <FormDivider
                  v-if="input.collection === 'form_divider'"
                  :data="input.item"
                  class="col-span-2"
                />
                <FormTextInput
                  v-else-if="input.collection === 'form_text_input'"
                  :data="input.item"
                  :class="input.item.width === 'full' ? 'col-span-2' : '' "
                />
                <FormEmailInput
                  v-else-if="input.collection === 'form_email_input'"
                  :data="input.item"
                  :class="input.item.width === 'full' ? 'col-span-2' : '' "
                />
                <FormTextareaInput
                  v-else-if="input.collection === 'form_textarea_input'"
                  :data="input.item"
                  class="col-span-2"
                />
                <FormSingleCheckboxInput
                  v-else-if="input.collection === 'form_single_checkbox_input'"
                  :data="input.item"
                  class="col-span-2"
                />
                <FormAddressInput
                  v-else-if="input.collection === 'form_address_input'"
                  :data="input.item"
                  class="col-span-2"
                />
                <FormCheckboxGroupInput
                  v-else-if="input.collection === 'form_checkbox_group_input'"
                  :data="input.item"
                  class="col-span-2"
                />
                <template v-else-if="input.collection === 'form_special_dealer_selector'">
                  <template v-if="activatedTeleport">
                    <Teleport :to="`#teleport-${id}`">
                      <FormSpecialDealerSelector
                        :data="input.item"
                        @next="showStep1 = false"
                      />
                    </Teleport>
                  </template>
                </template>

                <template v-else-if="input.collection === 'form_special_checkbox_choice'">
                  <template v-if="activatedTeleport">
                    <Teleport :to="`#teleport-left-${id}`">
                      <FormSpecialCheckboxChoice :data="input.item" />
                    </Teleport>
                  </template>
                </template>
              </template>
            </div>
          </FormKit>
        </ClientOnly>
      </div>

      <div
        v-else
        class="flex justify-center"
      >
        <div class="max-w-3xl">
          <TextWithAfterLine
            :text="dt(props.data.translations)?.label ?? ''"
            class="text-1.6rem"
          />

          <p class="mx-auto mt-4rem inline-block text-2.5rem font-title">
            {{ dt(props.data.translations)?.text }}
          </p>
        </div>
      </div>
    </Container>
  </BlockBase>
</template>
